import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";

import CustomerSelector from "./CustomerSelector";
import SessionManager from "./SessionManager";
import ChatWindow from "./ChatWindow";
import CustomerGallery from "./CustomerGallery";

export default function DesignStudio({ apiBase = "/api" }) {
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [sessionId, setSessionId] = useState(null);

  // Images queued to go out with the next chat message, regardless of
  // whether they came from a local upload or the gallery's "Attach" button.
  const [attachedImages, setAttachedImages] = useState([]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const customer = searchParams.get("customer");
    const session = searchParams.get("session");

    if (customer) setCustomerId(customer);
    if (session) setSessionId(session);
  }, []);

  // Resolve the customer's display name. Covers both the normal selection
  // flow and the deep-link case (customer id arrives via the URL only).
  useEffect(() => {
    if (!customerId) {
      setCustomerName("");
      return;
    }

    let cancelled = false;

    fetch(`${apiBase}/customers/`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const match = (data || []).find(
          (c) => String(c.id) === String(customerId),
        );
        setCustomerName(match ? match.name : "");
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [customerId, apiBase]);

  const addAttachedFiles = (files) => {
    setAttachedImages((prev) => [
      ...prev,
      ...files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
  };

  const attachGalleryImage = async (img) => {
    const res = await fetch(img.url);
    const blob = await res.blob();
    const file = new File([blob], `gallery-${img.id}.png`, {
      type: blob.type || "image/png",
    });

    setAttachedImages((prev) => [...prev, { file, preview: img.url }]);
  };

  const removeAttachedImage = (index) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Container fluid className="mt-3">
      <Row>
        {/* LEFT PANEL */}
        <Col md={3}>
          <CustomerSelector
            apiBase={apiBase}
            onSelect={(id, name) => {
              setCustomerId(id);
              setCustomerName(name || "");
              setSessionId(null); // reset session when customer changes
              setAttachedImages([]);
            }}
          />

          <SessionManager
            apiBase={apiBase}
            customerId={customerId}
            onSelectSession={setSessionId}
          />
        </Col>

        {/* CENTER CHAT */}
        <Col md={6}>
          <ChatWindow
            apiBase={apiBase}
            customerId={customerId}
            sessionId={sessionId}
            setSessionId={setSessionId}
            attachedImages={attachedImages}
            onAddFiles={addAttachedFiles}
            onRemoveAttachedImage={removeAttachedImage}
            onClearAttachedImages={() => setAttachedImages([])}
          />
        </Col>

        {/* RIGHT GALLERY */}
        <Col md={3}>
          <CustomerGallery
            apiBase={apiBase}
            customerId={customerId}
            customerName={customerName}
            onAttach={attachGalleryImage}
          />
        </Col>
      </Row>
    </Container>
  );
}
